import React, { useState, useMemo } from 'react';

function ReservationList({ reservations, onCancel, onCheckIn, onCheckOut, onNewReservation }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('check_in');
  const [sortOrder, setSortOrder] = useState('asc');
  const getStatusBadge = (status) => {
    const statusMap = {
      confirmed: { label: 'Confirmée', class: 'status-confirmed' },
      checked_in: { label: 'Check-in effectué', class: 'status-checked-in' },
      checked_out: { label: 'Check-out effectué', class: 'status-checked-out' },
      cancelled: { label: 'Annulée', class: 'status-cancelled' }
    };

    const statusInfo = statusMap[status] || { label: status, class: 'status-default' };

    return (
      <span className={`reservation-status-badge ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    );
  };

  const calculateNights = (checkIn, checkOut) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  const isToday = (date) => {
    const today = new Date();
    const checkDate = new Date(date);
    return checkDate.toDateString() === today.toDateString();
  };

  // Filtrage et recherche des réservations
  const filteredAndSortedReservations = useMemo(() => {
    let filtered = reservations.filter(reservation => {
      // Recherche textuelle
      const searchMatch = !searchTerm ||
        reservation.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reservation.guest_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reservation.room_number?.toString().includes(searchTerm.toLowerCase());

      // Filtre par statut
      const statusMatch = statusFilter === 'all' || reservation.status === statusFilter;

      // Filtre par date
      let dateMatch = true;
      const today = new Date();
      const checkInDate = new Date(reservation.check_in);
      const checkOutDate = new Date(reservation.check_out);

      switch (dateFilter) {
        case 'today':
          dateMatch = isToday(reservation.check_in) || isToday(reservation.check_out);
          break;
        case 'this_week':
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          dateMatch = checkInDate >= weekStart && checkInDate <= weekEnd;
          break;
        case 'upcoming':
          dateMatch = checkInDate >= today;
          break;
        case 'past':
          dateMatch = checkOutDate < today;
          break;
        default:
          dateMatch = true;
      }

      return searchMatch && statusMatch && dateMatch;
    });

    // Tri
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'guest_name':
          aVal = a.guest_name.toLowerCase();
          bVal = b.guest_name.toLowerCase();
          break;
        case 'room_number':
          aVal = parseInt(a.room_number);
          bVal = parseInt(b.room_number);
          break;
        case 'check_in':
          aVal = new Date(a.check_in);
          bVal = new Date(b.check_in);
          break;
        case 'check_out':
          aVal = new Date(a.check_out);
          bVal = new Date(b.check_out);
          break;
        case 'total_amount':
          aVal = (a.total_amount || 0) + (a.extra_charges || 0);
          bVal = (b.total_amount || 0) + (b.extra_charges || 0);
          break;
        default:
          aVal = a[sortBy];
          bVal = b[sortBy];
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [reservations, searchTerm, statusFilter, dateFilter, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFilter('all');
    setSortBy('check_in');
    setSortOrder('asc');
  };

  return (
    <div className="reservation-layout">
      {/* Header inspiré de room-layout */}
      <div className="reservation-filters">
        <div className="filters-header">
          <div className="filters-left">
            <h3>📋 Gestion des Réservations</h3>
            <div className="results-info">
              <span className="room-count">
                {filteredAndSortedReservations.length} réservation(s)
                {reservations.length !== filteredAndSortedReservations.length &&
                  ` sur ${reservations.length} au total`}
              </span>
              {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && (
                <span className="filter-badge">Filtré</span>
              )}
            </div>
          </div>
          <div className="filters-right">
            <button
              className="btn btn-primary"
              onClick={onNewReservation}
            >
              + Nouvelle Réservation
            </button>
          </div>
        </div>

        {/* Ligne de filtres */}
        <div className="filters-row">
          <div className="filter-group">
            <label>Rechercher</label>
            <input
              type="text"
              className="search-input"
              placeholder="Nom, email, ou numéro de chambre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Statut</label>
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="confirmed">Confirmées</option>
              <option value="checked_in">Check-in effectué</option>
              <option value="checked_out">Check-out effectué</option>
              <option value="cancelled">Annulées</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Période</label>
            <select
              className="filter-select"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="this_week">Cette semaine</option>
              <option value="upcoming">À venir</option>
              <option value="past">Passées</option>
            </select>
          </div>

          <div className="filter-actions">
            <button
              className="btn"
              onClick={resetFilters}
              title="Réinitialiser tous les filtres"
            >
              X
            </button>
          </div>
        </div>

        {/* Filtres rapides */}
        <div className="quick-filters">
          <span className="quick-label">Filtres rapides :</span>
          <button
            className={`quick-filter ${dateFilter === 'today' ? 'active' : ''}`}
            onClick={() => setDateFilter(dateFilter === 'today' ? 'all' : 'today')}
          >
            Aujourd'hui
          </button>
          <button
            className={`quick-filter ${statusFilter === 'confirmed' ? 'active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'confirmed' ? 'all' : 'confirmed')}
          >
            À confirmer
          </button>
          <button
            className={`quick-filter ${statusFilter === 'checked_in' ? 'active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'checked_in' ? 'all' : 'checked_in')}
          >
            En cours
          </button>
        </div>
      </div>

      {filteredAndSortedReservations.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          Aucune réservation trouvée
        </div>
      ) : (
        <div className="reservations-table-container">
          <table className="reservations-table">
            <thead>
              <tr>
                <th
                  className="sortable-header"
                  onClick={() => handleSort('guest_name')}
                >
                  Client {getSortIcon('guest_name')}
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort('room_number')}
                >
                  Chambre {getSortIcon('room_number')}
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort('check_in')}
                >
                  Arrivée {getSortIcon('check_in')}
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort('check_out')}
                >
                  Départ {getSortIcon('check_out')}
                </th>
                <th>Nuits</th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort('total_amount')}
                >
                  Total {getSortIcon('total_amount')}
                </th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedReservations.map(reservation => (
              <tr 
                key={reservation.id}
                style={{
                  backgroundColor: isToday(reservation.check_in) || isToday(reservation.check_out) 
                    ? '#fff3cd' : 'white'
                }}
              >
                <td>
                  <div>
                    <strong>{reservation.guest_name}</strong>
                    {reservation.guest_email && (
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {reservation.guest_email}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <strong>{reservation.room_number}</strong>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>
                    {reservation.room_type}
                  </div>
                </td>
                <td>
                  {new Date(reservation.check_in).toLocaleDateString()}
                  {isToday(reservation.check_in) && (
                    <div style={{ fontSize: '0.8rem', color: '#28a745', fontWeight: 'bold' }}>
                      Aujourd'hui
                    </div>
                  )}
                </td>
                <td>
                  {new Date(reservation.check_out).toLocaleDateString()}
                  {isToday(reservation.check_out) && (
                    <div style={{ fontSize: '0.8rem', color: '#dc3545', fontWeight: 'bold' }}>
                      Aujourd'hui
                    </div>
                  )}
                </td>
                <td>{calculateNights(reservation.check_in, reservation.check_out)}</td>
                <td>
                  €{((reservation.total_amount || 0) + (reservation.extra_charges || 0)).toFixed(2)}
                  {reservation.extra_charges > 0 && (
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                      + €{reservation.extra_charges} charges
                    </div>
                  )}
                </td>
                <td>{getStatusBadge(reservation.status)}</td>
                <td>
                  <div className="actions-cell">
                    {reservation.status === 'confirmed' && (
                      <button 
                        className="btn btn-success btn-small"
                        onClick={() => onCheckIn(reservation.id)}
                        title="Effectuer le check-in"
                      >
                        Check-in
                      </button>
                    )}
                    
                    {reservation.status === 'checked_in' && (
                      <button 
                        className="btn btn-danger btn-small"
                        onClick={() => onCheckOut(reservation.id)}
                        title="Effectuer le check-out"
                      >
                        Check-out
                      </button>
                    )}
                    
                    {(reservation.status === 'confirmed' || reservation.status === 'reserved') && (
                      <button 
                        className="btn btn-secondary btn-small"
                        onClick={() => onCancel(reservation.id)}
                        title="Annuler la réservation"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

export default ReservationList;